import { Spinner } from '@chakra-ui/react'
import { Component } from 'react'
import { Encodings, OrderUserCounters } from 'stylewhere/api'
import { Box, EmptyList, Modal, Tab, Text } from 'stylewhere/components'
import { T, __ } from 'stylewhere/i18n'
import { EncodingUserCountersOrder } from './EncodingUserCountersOrder'
import styled from '@emotion/styled'
import { AppStore } from 'stylewhere/shared'

interface Props {
  onClose: () => void
}

interface State {
  rows: OrderUserCounters[]
  loading: boolean
  loadingData: boolean
  tabs: { value: string; label: string; active: boolean }[]
  minTableWidth: number
}

const MAX_WIDTH_PANEL = 1800

export class EncodingUserCounters extends Component<Props, State> {
  loadData = false

  state: State = {
    rows: [],
    loading: false,
    loadingData: false,
    tabs: [
      { value: 'user', label: __(T.misc.encoding_user), active: true },
      { value: 'place', label: __(T.misc.encoding_place), active: false }
    ],
    minTableWidth: Math.min(MAX_WIDTH_PANEL, window.innerWidth - 60)
  }

  componentDidMount() {
    window.addEventListener('resize', this.onWindowResize)
    if (this.loadData) return
    this.loadData = true
    this.setState({ loading: true }, this._init)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize)
  }

  onWindowResize = () => {
    this.setState({ minTableWidth: Math.min(MAX_WIDTH_PANEL, window.innerWidth - 60) })
  }

  _init = async () => {
    try {
      const now = new Date()
      const pad = (n: number) => String(n).padStart(2, '0')
      const fromDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T00:00:00.000+0000`
      const { tabs } = this.state
      let res
      if (tabs[0].active) res = await Encodings.userCounters(fromDate)
      else res = await Encodings.placeCounters(AppStore.defaultWorkstation?.placeId, fromDate)
      this.setState({ rows: res ?? [], loading: false, loadingData: false })
    } catch {
      this.setState({ rows: [], loading: false, loadingData: false })
    } finally {
      this.loadData = false
    }
  }

  orderClick = (index: number) => {
    this.setState(({ rows }) => ({
      rows: rows.map((row, i) => ({ ...row, expanded: i === index ? !row.expanded : false }))
    }))
  }

  onTabClick = (tab: string) => {
    this.setState(
      (prevState) => ({
        tabs: prevState.tabs.map((opt) => ({ ...opt, active: opt.value === tab })),
        loadingData: true
      }),
      this._init
    )
  }

  renderLoading = () => {
    return (
      <Box center vcenter mt={40} mb={40}>
        <Spinner color="#e0e0e0" size="xl" />
      </Box>
    )
  }

  render() {
    const { onClose } = this.props
    const { rows, loading, loadingData, tabs, minTableWidth } = this.state
    const totalEncodings = rows.reduce((sum, row) => sum + (row.encodings ?? 0), 0)
    return (
      <Modal
        closeOnOverlayClick
        visible
        onClose={onClose}
        size="6xl"
        minModalContentWidth={minTableWidth}
        padding={'15px'}
        bodyStyle={{ margin: '0px 0px 0px 0px', padding: 0 }}
        paddingDivContent={0}
      >
        {loading ? (
          this.renderLoading()
        ) : (
          <>
            <Box mb={15} row vcenter justify="space-between">
              <Box mr={0} vcenter>
                <Tab
                  variant="secondary"
                  type="square"
                  options={tabs}
                  onOptionSelected={(opt) => this.onTabClick(opt)}
                  style={{ backgroundColor: '#eaeaea', border: 0, flex: 0, padding: 10, gap: 10 }}
                />
              </Box>
              {!loadingData && (
                <Box row vcenter>
                  <Text fontSize={18} style={{ color: '#454545', marginRight: 8 }}>
                    {__(T.misc.total_encodings)}:
                  </Text>
                  <Text bold fontSize={20} data-testid="encoding-user-counters-total">
                    {totalEncodings}
                  </Text>
                </Box>
              )}
            </Box>
            {loadingData ? (
              this.renderLoading()
            ) : rows.length === 0 ? (
              <EmptyList
                style={{
                  gap: 10,
                  backgroundColor: 'transparent',
                  marginTop: 30,
                  marginBottom: 20
                }}
              />
            ) : (
              <>
                <SessionRow row vcenter>
                  <Box row vcenter flex>
                    <Session row flex vcenter>
                      <Box width={30} vcenter></Box>
                      <Box width={300}>{__(T.misc.production_order)}</Box>
                      <Box width={200} center>
                        {__(T.misc.encodings)}
                      </Box>
                      <Box width={200} center>
                        {__(T.misc.progress)}
                      </Box>
                      <Box width={100} center>
                        {__(T.misc.detail)}
                      </Box>
                    </Session>
                  </Box>
                </SessionRow>
                {rows.map((row, i) => (
                  <EncodingUserCountersOrder key={'table-row' + i} order={row} index={i} onClick={this.orderClick} />
                ))}
              </>
            )}
          </>
        )}
      </Modal>
    )
  }
}

const SessionRow = styled(Box)`
  border-radius: 10px;
  border-bottom: 1px solid #f6f6f6;
  padding: 15px 0px;
  margin-bottom: 0px;
`

const Session = styled(Box)`
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  color: #454545;
  margin-left: 7px;
`
